import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { settings } from "../../helpers/settings";
import authHeader from "../../helpers/functions/auth-header";
import { toast } from "../../helpers/swal";
import { isLocalCarrierOrder } from "../../helpers/functions/is-local-carrier";

const dateToInput = (val) => {
  if (!val) return "";
  const d = new Date(val);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString().split("T")[0];
};

const UncommittedOrdersPage = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [savingNotes, setSavingNotes] = useState({});
  const [savingPrinter, setSavingPrinter] = useState({});
  const [search, setSearch] = useState("");
  const [sortColumn, setSortColumn] = useState("email_date");
  const [sortOrder, setSortOrder] = useState("desc");
  const [editedDates, setEditedDates] = useState({});
  const [editedNotes, setEditedNotes] = useState({});
  const [editedPrinters, setEditedPrinters] = useState({});
  const [clickedLinks, setClickedLinks] = useState(
    new Set(JSON.parse(localStorage.getItem("clickedLinks")) || [])
  );

  const printerOptions = [
    { value: "", label: "Select Printer" },
    { value: "Printer 1", label: "Printer 1" },
    { value: "Printer 2", label: "Printer 2" },
    { value: "Printer 3", label: "Printer 3" },
    { value: "Printer 4", label: "Printer 4" },
    { value: "Printer 5", label: "Printer 5" },
    { value: "Printer 6", label: "Printer 6" },
    { value: "UV Printer", label: "UV Printer" },
  ];

  useEffect(() => {
    const fetchUncommitted = async () => {
      setLoading(true);
      try {
        const res = await axios.get(
          `${settings.jiffyAPI}/get_uncommitted_orders.php`,
          { headers: authHeader() }
        );
        if (res.data?.success) {
          const data = res.data.orders || [];
          setOrders(
            data.map((o) => ({
              ...o,
              commitment_date: o.commitment_date ?? null,
              notes: o.notes ?? "",
              printer: o.printer ?? "",
            }))
          );
          const initialDates = {};
          const initialNotes = {};
          const initialPrinters = {};
          data.forEach((o) => {
            initialDates[o.po_number] = dateToInput(o.commitment_date);
            initialNotes[o.po_number] = o.notes || "";
            initialPrinters[o.po_number] = o.printer || "";
          });
          setEditedDates(initialDates);
          setEditedNotes(initialNotes);
          setEditedPrinters(initialPrinters);
        } else {
          setOrders([]);
        }
      } catch (e) {
        toast("Failed to fetch uncommitted orders", "error");
      } finally {
        setLoading(false);
      }
    };
    fetchUncommitted();
  }, []);

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(column);
      setSortOrder("asc");
    }
  };

  const handleLinkClick = (fileUrl) => {
    setClickedLinks((prev) => {
      const next = new Set(prev);
      next.add(fileUrl);
      localStorage.setItem("clickedLinks", JSON.stringify([...next]));
      return next;
    });
  };

  const filtered = useMemo(() => {
    const lower = search.trim().toLowerCase();
    let list = !lower
      ? orders
      : orders.filter(
          (o) =>
            o.po_number?.toLowerCase().includes(lower) ||
            o.name?.toLowerCase().includes(lower) ||
            o.order_type?.toLowerCase().includes(lower)
        );

    const cmp = (a, b) => {
      let av = a[sortColumn];
      let bv = b[sortColumn];

      if (sortColumn === "total_length") {
        av = parseFloat(av || 0);
        bv = parseFloat(bv || 0);
      } else if (
        sortColumn === "email_date" ||
        sortColumn === "commitment_date"
      ) {
        av = av ? new Date(av).getTime() : 0;
        bv = bv ? new Date(bv).getTime() : 0;
      } else {
        av = (av ?? "").toString().toLowerCase();
        bv = (bv ?? "").toString().toLowerCase();
      }
      if (av < bv) return sortOrder === "asc" ? -1 : 1;
      if (av > bv) return sortOrder === "asc" ? 1 : -1;
      return 0;
    };

    return [...list].sort(cmp);
  }, [orders, search, sortColumn, sortOrder]);

  const onEditDate = (po, val) => {
    setEditedDates((prev) => ({ ...prev, [po]: val }));
  };

  const onEditNotes = (po, val) => {
    setEditedNotes((prev) => ({ ...prev, [po]: val }));
  };

  const onEditPrinter = (po, val) => {
    setEditedPrinters((prev) => ({ ...prev, [po]: val }));
  };

  const saveDate = async (order) => {
    const po = order.po_number;
    const newDate = editedDates[po] || null;

    if (!newDate) {
      toast("Please select a date", "warning");
      return;
    }

    setSaving((s) => ({ ...s, [po]: true }));
    try {
      const res = await axios.post(
        `${settings.jiffyAPI}/update_commitment.php`,
        {
          po_number: po,
          commitment_date: newDate,
          notes: order.notes,
          printer: order.printer,
        },
        { headers: authHeader() }
      );

      if (res.data?.success) {
        setOrders((prev) =>
          prev.map((o) =>
            o.po_number === po ? { ...o, commitment_date: newDate } : o
          )
        );
        toast("Commitment date updated", "success");
      } else {
        toast(res.data?.error || "Update failed", "error");
      }
    } catch (e) {
      toast("Failed to update commitment date", "error");
    } finally {
      setSaving((s) => ({ ...s, [po]: false }));
    }
  };

  const saveNotes = async (order) => {
    const po = order.po_number;
    const newNotes = editedNotes[po] || "";

    setSavingNotes((s) => ({ ...s, [po]: true }));
    try {
      const res = await axios.post(
        `${settings.jiffyAPI}/update_commitment.php`,
        {
          po_number: po,
          commitment_date: order.commitment_date,
          notes: newNotes,
          printer: order.printer,
        },
        { headers: authHeader() }
      );

      if (res.data?.success) {
        setOrders((prev) =>
          prev.map((o) => (o.po_number === po ? { ...o, notes: newNotes } : o))
        );
        toast("Notes updated", "success");
      } else {
        toast(res.data?.error || "Update failed", "error");
      }
    } catch (e) {
      toast("Failed to update notes", "error");
    } finally {
      setSavingNotes((s) => ({ ...s, [po]: false }));
    }
  };

  const savePrinter = async (order) => {
    const po = order.po_number;
    const newPrinter = editedPrinters[po] || "";

    setSavingPrinter((s) => ({ ...s, [po]: true }));
    try {
      const res = await axios.post(
        `${settings.jiffyAPI}/update_commitment.php`,
        {
          po_number: po,
          commitment_date: order.commitment_date,
          notes: order.notes,
          printer: newPrinter,
        },
        { headers: authHeader() }
      );

      if (res.data?.success) {
        setOrders((prev) =>
          prev.map((o) =>
            o.po_number === po ? { ...o, printer: newPrinter } : o
          )
        );
        toast("Printer updated", "success");
      } else {
        toast(res.data?.error || "Update failed", "error");
      }
    } catch (e) {
      toast("Failed to update printer", "error");
    } finally {
      setSavingPrinter((s) => ({ ...s, [po]: false }));
    }
  };

  const renderSortArrow = (column) => {
    if (sortColumn !== column) return null;
    return sortOrder === "asc" ? " ↑" : " ↓";
  };

  return (
    <div className="container-fluid mt-4 px-2">
      <div className="text-center my-4">
        <h2>Uncommitted Orders</h2>
        <p className="text-muted">
          Orders with missing commitment date. Set and save per row.
        </p>
      </div>

      <div className="d-flex justify-content-between align-items-center mb-3">
        <input
          type="text"
          className="form-control w-auto"
          placeholder="Search by PO, Name, or Type"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <div className="d-flex align-items-center">
          <span className="me-3">
            <strong>Total:</strong> {filtered.length}
          </span>
          <span className="text-danger">
            <strong>Still Unset:</strong>{" "}
            {filtered.filter((o) => !o.commitment_date).length}
          </span>
        </div>
      </div>

      {loading ? (
        <p>Loading uncommitted orders...</p>
      ) : (
        <div className="table-responsive" style={{ overflowX: "auto" }}>
          <table className="table table-bordered w-100">
            <thead>
              <tr>
                <th
                  style={{ minWidth: "140px", cursor: "pointer" }}
                  onClick={() => handleSort("po_number")}
                >
                  PO Number{renderSortArrow("po_number")}
                </th>
                <th
                  style={{ minWidth: "160px", cursor: "pointer" }}
                  onClick={() => handleSort("order_type")}
                >
                  Order Type{renderSortArrow("order_type")}
                </th>
                <th
                  style={{ minWidth: "160px", cursor: "pointer" }}
                  onClick={() => handleSort("name")}
                >
                  Customer Name{renderSortArrow("name")}
                </th>
                <th
                  style={{ minWidth: "120px", cursor: "pointer" }}
                  onClick={() => handleSort("total_length")}
                >
                  Total Length{renderSortArrow("total_length")}
                </th>
                <th
                  style={{ minWidth: "130px", cursor: "pointer" }}
                  onClick={() => handleSort("email_date")}
                >
                  Mail Received{renderSortArrow("email_date")}
                </th>
                <th style={{ minWidth: "160px" }}>Label</th>
                <th style={{ minWidth: "200px" }}>DTF Files</th>
                <th
                  style={{ minWidth: "170px", cursor: "pointer" }}
                  onClick={() => handleSort("commitment_date")}
                >
                  Commitment Date{renderSortArrow("commitment_date")}
                </th>
                <th style={{ minWidth: "110px" }}>Actions</th>
                <th
                  style={{ minWidth: "200px", cursor: "pointer" }}
                  onClick={() => handleSort("notes")}
                >
                  Notes{renderSortArrow("notes")}
                </th>
                <th style={{ minWidth: "110px" }}>Notes Action</th>
                <th
                  style={{ minWidth: "150px", cursor: "pointer" }}
                  onClick={() => handleSort("printer")}
                >
                  Printer{renderSortArrow("printer")}
                </th>
                <th style={{ minWidth: "110px" }}>Printer Action</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((order) => (
                <tr key={order.po_number}>
                  <td>{order.po_number}</td>
                  <td>
                    {isLocalCarrierOrder(order)
                      ? "Local Carrier"
                      : order.order_type}
                  </td>
                  <td>{order.name}</td>
                  <td>{order.total_length} inches</td>
                  <td>
                    {order.email_date}
                    <br />
                    {order.email_time?.slice(0, 5)}
                  </td>
                  <td>
                    {order.attachment ? (
                      <a
                        href={`${settings.jiffyAPI}/${order.attachment}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          display: "inline-block",
                          color: clickedLinks.has(order.attachment)
                            ? "gray"
                            : "blue",
                        }}
                        onClick={() =>
                          handleLinkClick(
                            `${settings.jiffyAPI}/${order.attachment}`
                          )
                        }
                      >
                        Download Label
                      </a>
                    ) : (
                      <>
                        {/^\d+M(?:[-_A-Z0-9]+)?$/i.test(order.po_number) &&
                          order.shipment_label_link && (
                            <div>
                              <a
                                href={`${settings.jiffyAPI}/${order.shipment_label_link}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                  display: "inline-block",
                                  color: clickedLinks.has(order.attachment)
                                    ? "gray"
                                    : "blue",
                                }}
                                onClick={() =>
                                  handleLinkClick(
                                    `${settings.jiffyAPI}/${order.shipment_label_link}`
                                  )
                                }
                              >
                                Shipment Label
                              </a>
                            </div>
                          )}
                        {/^\d+M(?:[-_A-Z0-9]+)?$/i.test(order.po_number) &&
                          order.print_placement_link && (
                            <div>
                              <a
                                href={`${settings.jiffyAPI}/${order.print_placement_link}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                  display: "inline-block",
                                  color: clickedLinks.has(order.attachment)
                                    ? "gray"
                                    : "blue",
                                }}
                                onClick={() =>
                                  handleLinkClick(
                                    `${settings.jiffyAPI}/${order.print_placement_link}`
                                  )
                                }
                              >
                                Print Placement
                              </a>
                            </div>
                          )}
                      </>
                    )}
                  </td>
                  <td>
                    {(order.gangsheets || []).map((file, i) => (
                      <div key={i}>
                        <a
                          href={file.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            color: clickedLinks.has(file.link)
                              ? "gray"
                              : "blue",
                          }}
                          onClick={() => handleLinkClick(file.link)}
                        >
                          {file.label?.toLowerCase().includes("uv")
                            ? `Download UV Link-${i + 1}`
                            : `Download DTF Link-${i + 1}`}
                        </a>
                      </div>
                    ))}
                  </td>
                  <td>
                    <input
                      type="date"
                      className="form-control"
                      value={editedDates[order.po_number] || ""}
                      onChange={(e) =>
                        onEditDate(order.po_number, e.target.value)
                      }
                    />
                  </td>
                  <td>
                    <button
                      className="btn btn-sm btn-primary"
                      onClick={() => saveDate(order)}
                      disabled={!!saving[order.po_number]}
                    >
                      {saving[order.po_number] ? "Saving..." : "Save"}
                    </button>
                  </td>
                  <td>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Enter notes..."
                      value={editedNotes[order.po_number] || ""}
                      onChange={(e) =>
                        onEditNotes(order.po_number, e.target.value)
                      }
                      style={{ minWidth: "180px" }}
                    />
                  </td>
                  <td>
                    <button
                      className="btn btn-sm btn-success"
                      onClick={() => saveNotes(order)}
                      disabled={!!savingNotes[order.po_number]}
                    >
                      {savingNotes[order.po_number] ? "Saving..." : "Save"}
                    </button>
                  </td>
                  <td>
                    <select
                      className="form-control"
                      value={editedPrinters[order.po_number] || ""}
                      onChange={(e) =>
                        onEditPrinter(order.po_number, e.target.value)
                      }
                      style={{ minWidth: "130px" }}
                    >
                      {printerOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <button
                      className="btn btn-sm btn-warning"
                      onClick={() => savePrinter(order)}
                      disabled={!!savingPrinter[order.po_number]}
                    >
                      {savingPrinter[order.po_number] ? "Saving..." : "Save"}
                    </button>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && !loading && (
                <tr>
                  <td colSpan={13} className="text-center text-muted">
                    No uncommitted orders found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default UncommittedOrdersPage;
